import { getLocaleMeta } from "@/lib/i18n/locales";

/**
 * Calendar-day arithmetic for the study planners.
 *
 * A planner works in whole days — "start on the 5th, due on the 26th" — and JavaScript's
 * `Date` is an instant, which is where the classic off-by-one comes from:
 * `new Date("2026-10-05")` is midnight UTC, which is still the 4th in Los Angeles, and
 * adding 24 hours across a daylight-saving change lands an hour short of the next
 * midnight. Both bugs produce a plan that is a day off for some readers and correct on
 * the developer's machine.
 *
 * So a date here is a **day number**: whole days since 1970-01-01 in the proleptic
 * Gregorian calendar, built with `Date.UTC` and read back with the `getUTC*` getters.
 * Adding days is integer addition, the weekday is a modulo, and nothing depends on the
 * reader's time zone except `localToday`, which asks for it on purpose — "today" is the
 * reader's own date, and it is read once, after mount, never during render.
 */

export type DayNumber = number;

const MS_PER_DAY = 86_400_000;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar date as a day number, or null for 2026-02-30 and friends. */
export function dayFromParts(
  year: number,
  month: number,
  day: number
): DayNumber | null {
  if (
    !(
      Number.isInteger(year) &&
      Number.isInteger(month) &&
      Number.isInteger(day)
    )
  ) {
    return null;
  }
  // Four-digit years only: a date input mid-edit reports "0002-10-05", and years below
  // 100 are remapped to 1900–1999 by `Date.UTC`.
  if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1) {
    return null;
  }
  const ms = Date.UTC(year, month - 1, day);
  const date = new Date(ms);
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }
  return Math.round(ms / MS_PER_DAY);
}

/** `YYYY-MM-DD`, the value an `<input type="date">` holds, as a day number. */
export function parseIsoDay(value: string): DayNumber | null {
  const match = ISO_DATE.exec(value.trim());
  if (!match) {
    return null;
  }
  return dayFromParts(Number(match[1]), Number(match[2]), Number(match[3]));
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** The UTC calendar parts of a day number. */
export function dayParts(day: DayNumber): {
  year: number;
  month: number;
  day: number;
} {
  const date = new Date(day * MS_PER_DAY);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

/** A day number as `YYYY-MM-DD`. */
export function isoFromDay(day: DayNumber): string {
  const parts = dayParts(day);
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

/** A day number as `YYYYMMDD`, iCalendar's DATE form. */
export function compactFromDay(day: DayNumber): string {
  const parts = dayParts(day);
  return `${parts.year}${pad2(parts.month)}${pad2(parts.day)}`;
}

/** 0 = Sunday … 6 = Saturday, as `Date#getDay` numbers them. 1970-01-01 was a Thursday. */
export function weekdayOf(day: DayNumber): number {
  return (((day + 4) % 7) + 7) % 7;
}

/**
 * Monday-first positions (0 = Monday … 6 = Sunday) to `weekdayOf` numbers.
 *
 * The planners draw their weekday toggles Monday first, the way ISO 8601 and most
 * timetables do, so a toggle's index and a weekday number are different things.
 */
export function weekdayFromMondayIndex(index: number): number {
  return (index + 1) % 7;
}

/**
 * The reader's own date, from their clock and time zone.
 *
 * Deliberately the one local-time read in this module, and it must run in an effect or
 * an event handler: during render it would differ between the server and the browser.
 */
export function localToday(now: Date = new Date()): DayNumber {
  return (
    dayFromParts(now.getFullYear(), now.getMonth() + 1, now.getDate()) ??
    Math.floor(now.getTime() / MS_PER_DAY)
  );
}

/** Every day from `first` to `last`, inclusive, whose weekday is in `weekdays`. */
export function daysMatching(
  first: DayNumber,
  last: DayNumber,
  weekdays: ReadonlySet<number>,
  excluded: ReadonlySet<DayNumber> = new Set()
): DayNumber[] {
  const days: DayNumber[] = [];
  for (let day = first; day <= last; day += 1) {
    if (weekdays.has(weekdayOf(day)) && !excluded.has(day)) {
      days.push(day);
    }
  }
  return days;
}

/** `ceil(numerator / denominator)` for non-negative integers, without float drift. */
export function ceilDiv(numerator: number, denominator: number): number {
  const quotient = Math.floor(numerator / denominator);
  return numerator % denominator === 0 ? quotient : quotient + 1;
}

/**
 * A formatter for day numbers in the reader's language.
 *
 * Always `timeZone: "UTC"`, because a day number is midnight UTC: formatting it in local
 * time would show the previous day to everyone west of Greenwich. One `Intl` instance per
 * call site rather than per row — a semester plan has a few hundred rows.
 */
export function dayFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions
): (day: DayNumber) => string {
  const tag = getLocaleMeta(locale)?.hreflang ?? "en";
  let format: Intl.DateTimeFormat;
  try {
    format = new Intl.DateTimeFormat(tag, { ...options, timeZone: "UTC" });
  } catch {
    format = new Intl.DateTimeFormat("en", { ...options, timeZone: "UTC" });
  }
  return (day) => format.format(new Date(day * MS_PER_DAY));
}

/**
 * A random identifier for calendar UIDs and list rows, from the browser's CSPRNG.
 *
 * Event-handler only (it reads `crypto`), so the server never renders one.
 */
export function randomToken(length = 12): string {
  const bytes = new Uint8Array(Math.ceil(length / 2));
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, length);
}
